/**
 * Locale store.
 *
 * The locale is application state, not component state: the switch sits in the
 * topbar and every view must repaint when it changes. A `useState` inside the
 * hook would only repaint the switch itself, so the choice lives in one
 * module-level value that components subscribe to with `useSyncExternalStore`.
 */

/** Supported locales. */
export type Locale = "de" | "en";

const STORAGE_KEY = "locale";

/**
 * Read the locale the browser should start in:
 * 1. the stored preference, 2. `navigator.language`, 3. German.
 */
function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "de" || stored === "en") return stored;
  } catch {
    // A browser with site data blocked throws on read. Fall through.
  }
  if (typeof navigator !== "undefined" && navigator.language.toLowerCase().startsWith("en")) {
    return "en";
  }
  return "de";
}

let current: Locale = initialLocale();
const listeners = new Set<() => void>();

/** Current locale. */
export function getLocale(): Locale {
  return current;
}

/** Switch the locale, persist it, and repaint every subscriber. */
export function setLocale(next: Locale): void {
  if (next === current) return;
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Persisting is a convenience, never a precondition.
  }
  applyDocumentLang();
  for (const listener of listeners) listener();
}

/** Subscribe to locale changes. Returns the unsubscribe function. */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Stamp the locale on `<html lang>`. BITV 2.0 requires the page language to be
 * declared, and a screen reader picks its voice from this attribute.
 */
export function applyDocumentLang(): void {
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("lang", current);
  }
}
